import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { TFunction } from "core/entities";
import type { ExtractSuccessItemType } from "core/entities/APITypes";
import { useIntersectionObserver } from "core/hooks/useIntersectionObserver";
import { useCallback } from "react";
import { EventCard } from "./EventCard";

export function EventList({
  items,
  onArticleTypeClick,
  onCategoryClick,
  onIntersection,
  t,
}: {
  items: ExtractSuccessItemType<"/api/news-events/articles-v2/Events/public">[];
  onArticleTypeClick: (typeId: string) => void;
  onCategoryClick: (categoryId: string) => void;
  onIntersection?: () => void;
  t: TFunction;
}) {
  const intersectionCallBack = useCallback<IntersectionObserverCallback>(
    (entries) => {
      const lastEntry = entries.at(-1);
      const shouldLoadMore =
        lastEntry?.isIntersecting && typeof onIntersection === "function";
      if (shouldLoadMore) {
        onIntersection();
      }
    },
    [onIntersection]
  );

  const { ref: intersectionObserverRef } =
    useIntersectionObserver(intersectionCallBack);
  return (
    <Box
      component="section"
      sx={{
        marginX: "auto",
        display: "grid",
        justifyContent: "center",
        gap: 2,
        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
        mb: 2,
      }}
    >
      {items.length === 0 && (
        <Typography ml={1} color="text.secondary">
          {t("noItems")}
        </Typography>
      )}
      {items.map(
        (
          {
            id,
            title,
            blurb,
            banner,
            articleType,
            articleCategory,
            address,
            startDate,
            endDate,
            url,
            isImportant,
          },
          index
        ) => (
          <EventCard
            onChipClick={() => {
              if (articleType?.id) {
                onArticleTypeClick(articleType.id);
              }
            }}
            onCategoryClick={() => {
              if (articleCategory?.id) {
                onCategoryClick(articleCategory.id);
              }
            }}
            key={id}
            id={id ?? ""}
            ref={
              /* applying intersectionObserver ref to 4th last element */
              index === items.length - 5 ? intersectionObserverRef : undefined
            }
            startDate={startDate ?? ""}
            endDate={endDate ?? ""}
            title={title ?? ""}
            blurb={blurb ?? ""}
            address={address ?? ""}
            url={url ?? ""}
            articleType={articleType ?? { id: "", title: "", description: "" }}
            articleCategory={articleCategory}
            banner={banner}
            isImportant={isImportant}
          />
        )
      )}
    </Box>
  );
}
